import { Checkbox, Table, Tbody, Th, Thead, Tr } from '@chakra-ui/react';
import { useCallback } from 'react';
import { t } from '@lingui/core/macro';

import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { SortOrder } from '@duke-hq/lib/pagination';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

import { SupportTaskRow } from './SupportTaskRow';
import {
  SupportTaskProcessor,
  SupportTaskStatus,
  type SupportTaskKind,
} from '@duke-hq/svc-api-db/client';
import { isTaskAssignable } from '@duke-hq/svc-api/admin/trpc/routers/support/utils/taskUtils';

interface SortPropsInterface {
  orderBy?: string | undefined;
  sort?: SortOrder | undefined;
  page?: string | undefined;
  search?: string | undefined;
  processor?: SupportTaskProcessor | undefined;
  status?: SupportTaskStatus | undefined;
  kind?: SupportTaskKind | undefined;
  assignedTo?: string | undefined;
  scheduledDate?: string | undefined;
}

export function SupportTasksList({
  tasks,
  urlParams,
  onFilters,
  onTaskClick,
  selectedTaskIds = [],
  onSelectionChange,
}: {
  tasks: RouterOutputs['support']['task']['list']['items'];
  urlParams: SortPropsInterface;
  onFilters: (filters: SortPropsInterface) => void;
  onTaskClick?: (taskId: string) => void;
  selectedTaskIds?: string[];
  onSelectionChange?: (taskIds: string[]) => void;
}) {
  const { sort, orderBy, ...restParams } = urlParams;

  // Fetch the admin-user list once for the whole table and pass it to each row's assignee
  // dropdown, rather than having every row fetch and process it.
  const { data: users } = trpcAdmin.users.all.useQuery({}, { initialData: [] });

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        ...restParams,
        orderBy: name,
        sort: newSortDirection,
      } as SortPropsInterface;

      onFilters(updatedFilters);
    },
    [onFilters, restParams, sort],
  );

  const handleClick = useCallback(
    (taskId: string) => {
      onTaskClick?.(taskId);
    },
    [onTaskClick],
  );

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) {
      return;
    }

    // Only select assignable tasks (HUMAN processor tasks that are not COMPLETED)
    const assignableTasks = tasks.filter(isTaskAssignable);
    const assignableTaskIds = assignableTasks.map((task) => task.id);
    const allAssignableTasksSelected = assignableTaskIds.every((id) =>
      selectedTaskIds.includes(id),
    );

    if (allAssignableTasksSelected && assignableTaskIds.length > 0) {
      // Deselect all assignable tasks
      onSelectionChange(selectedTaskIds.filter((id) => !assignableTaskIds.includes(id)));
    } else {
      // Select all assignable tasks (keeping any other selected tasks)
      const newSelection = [...new Set([...selectedTaskIds, ...assignableTaskIds])];
      onSelectionChange(newSelection);
    }
  }, [tasks, selectedTaskIds, onSelectionChange]);

  const handleSelectTask = useCallback(
    (taskId: string, checked: boolean) => {
      if (!onSelectionChange) {
        return;
      }

      if (checked) {
        onSelectionChange([...selectedTaskIds, taskId]);
      } else {
        onSelectionChange(selectedTaskIds.filter((id) => id !== taskId));
      }
    },
    [selectedTaskIds, onSelectionChange],
  );

  // Only consider assignable tasks (HUMAN and not COMPLETED) for select-all state
  const assignableTasks = tasks.filter(isTaskAssignable);
  const assignableTaskIds = assignableTasks.map((task) => task.id);
  const selectedAssignableTaskIds = selectedTaskIds.filter((id) => assignableTaskIds.includes(id));

  const isAllSelected =
    assignableTasks.length > 0 && selectedAssignableTaskIds.length === assignableTasks.length;
  const isIndeterminate =
    selectedAssignableTaskIds.length > 0 &&
    selectedAssignableTaskIds.length < assignableTasks.length;

  return (
    <Table>
      <Thead>
        <Tr>
          <Th width="40px">
            {onSelectionChange ? (
              <Checkbox
                isChecked={isAllSelected}
                isIndeterminate={isIndeterminate}
                onChange={handleSelectAll}
              />
            ) : null}
          </Th>
          <Th>
            <SortableHeader
              label={t`Assignee`}
              columnName="assignedTo"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>{t`Participant`}</Th>
          <Th>
            <SortableHeader
              label={t`Status`}
              columnName="status"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>{t`Type`}</Th>
          <Th>{t`Contract type`}</Th>
          <Th>{t`Provider`}</Th>
          <Th>{t`Action`}</Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(tasks) && tasks.length > 0
          ? tasks.map((task) => (
              <SupportTaskRow
                key={task.id}
                task={task}
                users={users}
                onTaskClick={handleClick}
                isSelected={selectedTaskIds.includes(task.id)}
                onSelectChange={onSelectionChange ? handleSelectTask : undefined}
              />
            ))
          : null}
      </Tbody>
    </Table>
  );
}
